import { Button, DataTableController } from 'components/common'
import FocOrdersTable from 'components/focOrderManagement/focOrdersTable'
import ActiveFilterChip from 'components/common/activeFilterChip'
import { PlusIcon } from 'components/icons'
import {
  columns,
  FILTER_FIELD_FOC_ORDERS,
  FocOrderFilterForm,
} from 'components/focOrderManagement'
import { usePageAccess, useQuery } from 'hooks'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { PAGE_ADD_FOC_ORDER } from 'utils/constants'
import toast from 'react-hot-toast'
import { filterEmptyValues } from 'utils/helpers'
import { AxiosError } from 'axios'
import { exportFocOrders, triggerFocOrderSync } from 'utils/api/focApi'
import { handleError } from 'utils/helpers/handleError'

const CRON_TYPE = {
  FOC_ORDER: 'Foc Order Sync',
  RECONCILIATION: 'Reconciliation Sync',
}

const FocOrderList = () => {
  const { isEditor } = usePageAccess()
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [triggerLoading, setTriggerLoading] = useState<boolean>()

  const navigate = useNavigate()

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }
  const { getQuery } = useQuery()

  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_FOC_ORDERS)

    // if not filters found in URL
    if (!filters) {
      return toast.error('Filters are required to export data!', {
        duration: 3000,
      })
    }

    // filtering empty values and keys of filter
    const masterFilter = filterEmptyValues(filters)

    // if filters are present with empty values
    if (Object.keys(masterFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await exportFocOrders({ filters: masterFilter })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to export FOC Orders Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  const triggerCron = async (jobTitle: string) => {
    const loadingToast = toast.loading(`Initializing ${jobTitle} Job`, {
      duration: Infinity,
    })

    setTriggerLoading(true)

    try {
      await triggerFocOrderSync()

      toast.success(`${jobTitle} Job Initiated`, {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      handleError(error, loadingToast)
    } finally {
      setTriggerLoading(false)
    }
  }
  return (
    <>
      <Helmet>
        <title>GAG Aggregator | FOC Order Management</title>
      </Helmet>

      {isEditor && (
        <div className="flex items-center gap-2">
          <Button
            buttonText="Create New Order"
            icon={<PlusIcon />}
            className="mb-5"
            onClick={() => navigate(PAGE_ADD_FOC_ORDER.path)}
          />
          <div className="mb-5 flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              buttonText="Resync Orders"
              loading={triggerLoading}
              onClick={() => triggerCron(CRON_TYPE.FOC_ORDER)}
            />
          </div>
        </div>
      )}

      <div
        className={`${
          isEditor ? 'mt-4' : ''
        } mb-2 flex flex-wrap items-center justify-between  md:mb-0 md:flex-nowrap`}
      >
        <h3 className="title">FOC Orders</h3>
      </div>

      {/* Orders Table */}
      <DataTableController
        isEditor={isEditor}
        columns={columns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleExport={handleExport}
        activeColumnKey="focOrder"
        placeholder="Search by Order No and Magento/Aggregator Order No"
        defaultSelectedColumns={selectedColumns}
        loading={loading}
      />

      {!showFilter && <ActiveFilterChip filterKey={FILTER_FIELD_FOC_ORDERS} />}

      {showFilter ? (
        <>
          <FocOrderFilterForm toggleFilterForm={toggleFilterForm} />
        </>
      ) : (
        <FocOrdersTable
          setSelectedColumns={setSelectedColumns}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default FocOrderList
